import React, { useMemo } from 'react';
import cx from 'classnames';
import cloneDeep from 'lodash/cloneDeep';

import IconButton from '../../../../../../../../common/buttons/IconButton';
import Checkbox from '../../../../../../../../common/data-entry/InputSelectors/Checkbox';
import ModalBoxes from '../../../../../../../../common/feedback/ModalBoxes/ModalBoxes';
import useAppInfo from '../../../../../../../../common/hooks/useAppInfo';
import NotificationManager from '../../../../../../../../common/notifications/NotificationManager';
import { StyledTextViewer } from '../../../../../../../../common/RichTextFormat/viewer/StyledTextViewer';
import { resolveStyledFieldName } from '../../../../../../../../common/RichTextFormat/viewer/TextFieldResolver';
import { STYLED_INPUT_QUESTIONS } from '../../../../../../../../constants/inputTypes';
import { COMMENT_FIELD_IS_NOT_AVAILABLE } from '../../../../../../../../constants/notificationMessages';
import { scBlue100 } from '../../../../../../../../constants/systemColors';
import { validateQuestion } from '../../../../../services/questionUtils';
import CustomFieldConfiguration from '../../../CustomFieldConfiguration/CustomFieldConfiguration';
import EditCustomFieldModal from '../../../EditCustomFieldModal';
import { customClassObject } from '../../customClassObject';
import {
  FINANCE_COLUMN,
  FIXED_FIELD_COLUMN,
  INVESTIGATOR_FIELD_COLUMN,
  LOG_VIEW_COLUMN
} from '../FieldsToSelect/gridService';
import { EditFormAnswerView } from '../OptionalFields/EditFormAnswerView';

import FieldPreview from './FieldPreview';

export function CustomField({
  terminologyVersionGroupNumber,
  editMode,
  formLabelData,
  domain,
  labelIndex,
  item,
  index,
  getCheckboxForTableView,
  getInvestigatorFieldCheckboxForTableView,
  getExpenseFixedCheckboxForTableView,
  inputs,
  updateInputsAndPreviewData,
  updateState,
  onChangeLabelTextHandler,
  columnsToShow,
  onFinanceFieldChange
}) {
  const isTypeBlockLong = item.inputType === 'textBlockLong';
  const isTypeReminder = item.inputType === 'reminder';
  const isTypeCheckBox = item.inputType === 'checkbox';
  const isStyledQuestion = STYLED_INPUT_QUESTIONS.includes(item.inputType);
  const isLabelShown = !isTypeReminder && !isTypeCheckBox;
  const questionName = item.updatedQuestion ? item.updatedQuestion : item.question;
  const appInfo = useAppInfo();
  const itemGroupFinanceEventEnabled = appInfo?.features?.itemGroupFinanceEventEnabled;

  const { tableChckUniqId, fixedFieldChckUuid, financeUuid } = useMemo(function() {
    const uniqueId = crypto.randomUUID();
    return {
      tableChckUniqId: `table${uniqueId}`,
      fixedFieldChckUuid: `table${uniqueId}fixed`,
      financeUuid: `table${uniqueId}finance`
    };
  }, []);

  if (item.updatedQuestion) {
    item.question = item.updatedQuestion;
  }
  return (
    <>
      <div
        className={cx('igs-grid-row-inner-custom-fields-body', {
          'igs-grid-row-inner-custom-fields-body-readonly': !isLabelShown
        })}
      >
        <div className="igs-grid-row-column" data-column="CDASH_ITEMS">
          <div className="igs-grid-row-column-cdash-items-actions">
            <IconButton
              className="glyphicon glyphicon-edit mr-2"
              suit="glyphicon"
              color="blue"
              title="Edit CDASH Item"
              onClick={() => onEditCustomFormField(formLabelData, labelIndex)}
            >
              edit
            </IconButton>
          </div>
          <div
            data-testid="cdash-field-name"
            className={cx('igs-grid-row-column-cdash-items-question', {
              'overflow-ellipsis': !isTypeBlockLong,
              'overflow-ellipsis-multiple-line': isTypeBlockLong
            })}
          >
            {isLabelShown ? (
              isStyledQuestion ? (
                <StyledTextViewer value={resolveStyledFieldName(item.styledFieldName, questionName)} />
              ) : (
                questionName
              )
            ) : (
              <FieldPreview
                withoutWrapper
                labelText={item.label}
                data={item}
                disabled={true}
                formId={inputs[0].formId}
                formLabelId={formLabelData.formLableId}
                customClassObject={customClassObject}
                itemDefIndex={index}
                onChangeLabelTextHandler={e => onChangeLabelTextHandler(e.target.value, labelIndex, index, true)}
              />
            )}
          </div>
        </div>
        {columnsToShow.includes(FINANCE_COLUMN) && (
          <div className="igs-grid-row-column" data-column="FINANCE">
            {item.inputType === 'radio' && itemGroupFinanceEventEnabled && (
              <Checkbox
                color={scBlue100}
                checked={!!item.financeField}
                className="p-0 overflow-ellipsis"
                id={financeUuid}
                onChange={({ target }) => {
                  onFinanceFieldChange(target?.checked, item, 0, labelIndex, index, 'custom');
                }}
              />
            )}
          </div>
        )}
        <div className="igs-grid-row-column" data-column="CDASH">
          {item?.cdashAliasName}
        </div>
        {isLabelShown && (
          <div className="igs-grid-row-column" data-column="ANSWER_TYPE">
            <FieldPreview
              withoutWrapper
              labelText={item.label}
              data={item}
              disabled={true}
              formId={inputs[0].formId}
              formLabelId={formLabelData.formLableId}
              customClassObject={customClassObject}
              itemDefIndex={index}
              onChangeLabelTextHandler={e => onChangeLabelTextHandler(e.target.value, labelIndex, index, true)}
            />
          </div>
        )}
        <div className="igs-grid-row-column" data-column="ITEM_SEQUENCE">
          <EditFormAnswerView
            isExtensible={item.isExtensible}
            updateInputsAndPreviewData={updateInputsAndPreviewData}
            item={item}
            labelIndex={labelIndex}
            index={index}
            inputs={inputs}
            terminologyVersionGroupNumber={terminologyVersionGroupNumber}
            editMode={editMode}
            custom={true}
          />
          <CustomFieldConfiguration
            item={item}
            allInputs={inputs}
            onChange={() => updateState(updateInputsAndPreviewData(inputs))}
          />
          {domain !== 'ICF' && (
            <>
              <i className="material-icons formseqicon" title="Form View">
                library_books
              </i>
              <input
                type="text"
                readOnly
                value={item.sequence ? Number(item.sequence) : ''}
                title="Form Item Sequence"
                className="form-control igs-grid-sequence-field"
              />
            </>
          )}
        </div>
        {columnsToShow.includes(FIXED_FIELD_COLUMN) && (
          <div className="igs-grid-row-column" data-column="FIXED_FIELD">
            {getExpenseFixedCheckboxForTableView(item, fixedFieldChckUuid, labelIndex, index, 'custom')}
          </div>
        )}
        {columnsToShow.includes(INVESTIGATOR_FIELD_COLUMN) && (
          <div className="igs-grid-row-column" data-column="INVESTIGATOR_FIELD">
            {getInvestigatorFieldCheckboxForTableView(item, labelIndex, index, 'custom')}
          </div>
        )}
        {columnsToShow.includes(LOG_VIEW_COLUMN) && (
          <div className="igs-grid-row-column" data-column="LOG_VIEW">
            {getCheckboxForTableView(item, tableChckUniqId, labelIndex, index, 'custom')}
          </div>
        )}
      </div>
    </>
  );

  function onEditCustomFormField(customFieldData, index) {
    const customQuestion = customFieldData.customItemDefinationList[0];
    const modalBox = ModalBoxes.open({
      component: (
        <EditCustomFieldModal
          onSave={updateState => {
            const questionName = updateState.updatedCustomFieldName;
            const validationMessage = validateQuestion(inputs, questionName, function({ sequence }) {
              return customFieldData.sequence === sequence;
            });
            if (validationMessage) {
              if (validationMessage === COMMENT_FIELD_IS_NOT_AVAILABLE) {
                NotificationManager.error(validationMessage);
                return;
              }
              NotificationManager.warning(validationMessage);
            }
            const originInputs = cloneDeep(inputs);
            if (originInputs && originInputs[0] && originInputs[0].custom) {
              const customDomainElementToUpdate = originInputs[0].custom.find((customElement, innerIndex) => {
                return index === innerIndex;
              });

              const customElement = customDomainElementToUpdate.customItemDefinationList[0];
              const updatedCustomFieldName = updateState.updatedCustomFieldName;
              const updatedStyledFieldName = updateState.updatedStyledFieldName;

              if (customDomainElementToUpdate && customElement) {
                customElement.updatedQuestion = updatedCustomFieldName;
                if (STYLED_INPUT_QUESTIONS.includes(customElement.inputType)) {
                  customElement.styledFieldName = updatedStyledFieldName;
                }
                // For check box we need also update code element that is reflection of custom element //TODO
                if (
                  customElement.inputType === 'checkbox' &&
                  customElement.codeDefinationList &&
                  customElement.codeDefinationList.length === 1
                ) {
                  customElement.codeDefinationList[0].codedValue = updatedCustomFieldName;
                  customElement.codeDefinationList[0].decode = updatedCustomFieldName;
                  customElement.codeDefinationList[0].name = updatedCustomFieldName;
                }
                originInputs[0].custom[index] = customDomainElementToUpdate;
                updateInputsAndPreviewData(originInputs);
              }
            }
            modalBox.close();
          }}
          originalCustomFieldName={
            customQuestion.updatedQuestion ? customQuestion.updatedQuestion : customQuestion.question
          }
          styledFieldName={customQuestion.styledFieldName}
          isStyled={STYLED_INPUT_QUESTIONS.includes(customQuestion.inputType)}
        />
      )
    });
  }
}
